import React from "react";
import * as Separator from "@radix-ui/react-separator";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";

/* ===== Enlaces de centros: idioma-independientes ===== */
const centers = [
  { name: "Escola Pública La Daina", url: "https://www.google.com/maps/place/Escola+P%C3%BAblica+La+Daina/data=!4m2!3m1!19sChIJvfqLUp1PpRIRFCMzoQFzT7U" },
  { name: "Escola Pública Sant Domènec", url: "https://www.google.com/maps/place/Escola+P%C3%BAblica+Sant+Dom%C3%A8nec/data=!4m2!3m1!19sChIJ9YVfkwh3pBIRlOtZ2gSbXh0" },
  { name: "Institut Escola Llibertat", url: "https://maps.app.goo.gl/ZteaiuzEVrPVPuRA6" },
  { name: "Escola Espai 3", url: "https://maps.app.goo.gl/SNDQXiSx3afR8aNo9" },
  { name: "Escola Gabriel Castellà i Raich", url: "https://maps.app.goo.gl/eRdeh6jtFfNJqri9A" },
  { name: "Escola Xoriguer", url: "https://maps.app.goo.gl/Vp4DGXCvBDhv2Lqk6" },
  { name: "Escola Pública Consol Ferré", url: "https://maps.app.goo.gl/257e4hZX6VuBF3YW8" },
  { name: "Escola Joan Maragall", url: "https://maps.app.goo.gl/f6HjagYtaAWt6DLH9" },
  { name: "Col·legi Públic Joan Maragall", url: "https://maps.app.goo.gl/9HdWvcJHXr8QYaGb8" },
  { name: "Escola Primària i Infantil La Parellada", url: "https://maps.app.goo.gl/MP9UTjynJfcSU9Zn8" },
  { name: "Institut Escola El Cabrerès", url: "https://maps.app.goo.gl/ySUeff4pGu9hvxXUA" },
  { name: "Escola Pública Sant Domènec", url: "https://maps.app.goo.gl/CeznADftou17Lbo87" },
  { name: "Institut Escola Mas Rampinyo", url: "https://maps.app.goo.gl/tu2B6tNxLQpyxWWt8" },
];

const generalMapUrl = "https://maps.app.goo.gl/WAsZtoT8xEHdeAWb8";

/* ===== Tabla reutilizable ===== */
const Table = ({ columns, rows, totalLabel, totalValue, wrapColIndexes = [] }) => (
  <div className="rounded-2xl shadow-2xl ring-1 ring-black/5 bg-white/80 backdrop-blur">
    <table className="w-full table-auto border-separate border-spacing-y-2">
      <thead>
        <tr className="bg-emerald-700 text-white">
          {columns.map((c, i) => (
            <th
              key={`${c}-${i}`}
              className={
                "px-5 py-3 text-left text-sm font-semibold tracking-wide " +
                (i === 2 ? "whitespace-normal" : "whitespace-nowrap")
              }
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={idx} className="odd:bg-white even:bg-emerald-50/70 hover:bg-emerald-100/70 transition">
            {r.map((cell, i) => (
              <td
                key={i}
                className={
                  "px-5 py-3 align-top text-sm leading-snug " +
                  (wrapColIndexes.includes(i) ? "whitespace-normal break-words" : "whitespace-nowrap")
                }
                style={i === 2 ? { width: "50%" } : {}}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {totalLabel && (
        <tfoot>
          <tr className="bg-emerald-100">
            <td className="px-5 py-3 font-semibold whitespace-nowrap" colSpan={columns.length - 1}>
              {totalLabel}
            </td>
            <td className="px-5 py-3 font-semibold whitespace-nowrap">{totalValue}</td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

/* ===== Tarjeta de enlace moderna ===== */
const LinkCard = ({ name, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    className="group block rounded-2xl p-4 md:p-5 bg-gradient-to-br from-emerald-50 to-white hover:from-white hover:to-emerald-50 border border-emerald-100 shadow-sm hover:shadow-md transition transform hover:-translate-y-0.5"
    title={`${name} — Google Maps`}
  >
    <div className="flex items-start gap-3">
      <div className="shrink-0 rounded-xl p-2 bg-emerald-100 group-hover:bg-emerald-200 transition">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M14 3h7v7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 3l-9 9" stroke="currentColor" strokeWidth="2" />
          <path d="M5 12v7h7" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
      <div>
        <div className="font-medium text-emerald-900">{name}</div>
        <div className="text-sm text-emerald-700 opacity-90">Google Maps ↗</div>
      </div>
    </div>
  </a>
);

/* ===== Esquema Planificació (3 bloques) — internacionalizado ===== */
const PlanScheme = ({ blocks }) => (
  <div className="max-w-6xl mx-auto px-5">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {blocks.map((card, idx) => (
        <div key={idx} className="relative rounded-2xl overflow-hidden bg-white shadow-xl ring-1 ring-black/5">
          <div
            className={`h-2 bg-gradient-to-r ${
              idx === 0 ? "from-emerald-600 to-emerald-500" :
              idx === 1 ? "from-amber-600 to-amber-500" :
                           "from-sky-600 to-sky-500"
            }`}
          />
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center font-semibold">
                {idx + 1}
              </div>
              <h4 className="text-lg font-semibold">{card.title}</h4>
            </div>
            <div className="text-xs uppercase tracking-wider text-emerald-700 mb-3">{card.subtitle}</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {card.bullets?.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const STEAMcatPage = () => {
  const { t } = useTranslation();

  const schemeBlocks = t("steamcat.scheme.blocks", { returnObjects: true }) || [];
  const table1 = {
    columns: t("steamcat.phases.table1.columns", { returnObjects: true }) || [],
    rows: t("steamcat.phases.table1.rows", { returnObjects: true }) || [],
    totalLabel: t("steamcat.phases.table1.totalLabel"),
    totalValue: t("steamcat.phases.table1.totalValue"),
  };
  const table2 = {
    columns: t("steamcat.phases.table2.columns", { returnObjects: true }) || [],
    rows: t("steamcat.phases.table2.rows", { returnObjects: true }) || [],
    totalLabel: t("steamcat.phases.table2.totalLabel"),
    totalValue: t("steamcat.phases.table2.totalValue"),
  };
  const areas = t("steamcat.areas", { returnObjects: true }) || {};

  return (
    <div className="bg-gradient-to-b from-green-50 to-white opacity-90 min-h-screen relative">
      <div className="blob3" />

      <div className="max-w-7xl mx-auto px-5 py-36">
        {/* Título */}
        <h1 className={`text-center mb-14 ${styles.heroHeadText} normal-case`}>
          <span className="uppercase tracking-tight">STEAM</span>
          <span className="lowercase">cat</span>
        </h1>

        <Separator.Root className="bg-gray-300 h-px w-full mb-12" />

        {/* Imagen bajo el titular */}
        <div className="flex justify-center my-6">
          <figure className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5">
            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/10 via-transparent to-sky-500/10 pointer-events-none" />
            <img
              src="/images/Steamcat.png"
              alt={t("steamcat.intro.imageAlt", "STEAMcat")}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </figure>
        </div>

        {/* Intro */}
        <div className="max-w-6xl mx-auto px-5 space-y-7 text-justify">

          <h2 className="text-xl font-semibold text-black text-center">{t("steamcat.intro.heading")}</h2>
          <br/>
          <p className={styles.bodyText}>{t("steamcat.intro.p1")}</p>
          <p className={styles.bodyText}>{t("steamcat.intro.p2")}</p>
          <p className={styles.bodyText}>{t("steamcat.intro.p3")}</p>
          <p className={styles.bodyText}>{t("steamcat.intro.p4")}</p>
          <p className={styles.bodyText}>{t("steamcat.intro.p5")}</p>
<br/>

          <h2 className="text-xl font-semibold text-black">{t("steamcat.objective.title")}</h2>
          <p className={styles.bodyText}>{t("steamcat.objective.text")}</p>
        </div>

        <Separator.Root className="bg-gray-300 h-px w-full my-12" />

        {/* Centros + mapa */}
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <h2 className="text-2xl font-semibold text-black text-center w-full md:w-auto">
              {t("steamcat.centres.title")}
            </h2>
            <a
              href={generalMapUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-emerald-600 text-white shadow hover:bg-emerald-700 transition"
            >
              <span>{t("steamcat.centres.generalMap")}</span>
              <span aria-hidden>↗</span>
            </a>
          </div>

          <p className={`${styles.bodyText} mb-6`}>{t("steamcat.centres.description")}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {centers.map((c) => (
              <LinkCard key={c.name + c.url} name={c.name} url={c.url} />
            ))}
          </div>
        </div>

        <Separator.Root className="bg-gray-300 h-px w-full my-12" />

        {/* Esquema (3 bloques) */}
        <h2 className="text-2xl font-semibold text-black text-center mb-6">
          {t("steamcat.scheme.title")}
        </h2>
        <br/>

        <PlanScheme blocks={schemeBlocks} />

        <Separator.Root className="bg-gray-300 h-px w-full my-12" />
<br/>

        {/* Fases con bloques de texto (sin saltos) + tablas */}
        <div className="max-w-6xl mx-auto px-5 space-y-7 text-justify">
          {/* Fase 1 */}
          <h3 className="text-xl font-semibold text-black">{t("steamcat.phases.phase1.title")}</h3>
          <p className={`${styles.bodyText} whitespace-pre-line`}>
            {t("steamcat.phases.phase1.block")}
          </p>
          <Table
            columns={table1.columns}
            rows={table1.rows}
            totalLabel={table1.totalLabel}
            totalValue={table1.totalValue}
            wrapColIndexes={[2]}
          />
<br/>
<br/>

          {/* Fase 2 */}
          <h3 className="text-xl font-semibold text-black">{t("steamcat.phases.phase2.title")}</h3>
          <p className={`${styles.bodyText} whitespace-pre-line`}>
            {t("steamcat.phases.phase2.block")}
          </p>
          <Table
            columns={table2.columns}
            rows={table2.rows}
            totalLabel={table2.totalLabel}
            totalValue={table2.totalValue}
            wrapColIndexes={[2]}
          />
        </div>

        <Separator.Root className="bg-gray-300 h-px w-full my-12" />

        {/* A quién va dirigido, Áreas, Metodología, Resultados */}
        <div className="max-w-6xl mx-auto px-5 space-y-7 text-justify">
          <h2 className="text-2xl font-semibold text-black text-center">
            {t("steamcat.audience.title")}
          </h2>
          <p className={styles.bodyText}>{t("steamcat.audience.text")}</p>
<br/>

          {/* Áreas */}
          <h3 className="text-xl font-semibold text-black">{t("steamcat.areas.title")}</h3>

          <div>
            <div className={`${styles.bodyText} font-semibold`}>{t("steamcat.areas.env.title")}</div>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {(t("steamcat.areas.env.bullets", { returnObjects: true }) || []).map((b, i) => (
                <li key={i} className={styles.bodyText}>{b}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className={`${styles.bodyText} font-semibold`}>{t("steamcat.areas.math.title")}</div>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {(t("steamcat.areas.math.bullets", { returnObjects: true }) || []).map((b, i) => (
                <li key={i} className={styles.bodyText}>{b}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className={`${styles.bodyText} font-semibold`}>{t("steamcat.areas.community.title")}</div>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {(t("steamcat.areas.community.bullets", { returnObjects: true }) || []).map((b, i) => (
                <li key={i} className={styles.bodyText}>{b}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className={`${styles.bodyText} font-semibold`}>{t("steamcat.areas.sdg.title")}</div>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {(t("steamcat.areas.sdg.bullets", { returnObjects: true }) || []).map((b, i) => (
                <li key={i} className={styles.bodyText}>{b}</li>
              ))}
            </ul>
          </div>
<br/>

          {/* Metodología y resultados */}
          <h3 className="text-xl font-semibold text-black">{t("steamcat.methodology.title")}</h3>
          <ul className="list-disc list-inside space-y-2">
            <li className={styles.bodyText}>{t("steamcat.methodology.abp")}</li>
            <li className={styles.bodyText}>{t("steamcat.methodology.gamification")}</li>
            <li className={styles.bodyText}>{t("steamcat.methodology.steam")}</li>
          </ul>
<br/>

          <h3 className="text-xl font-semibold text-black">{t("steamcat.results.title")}</h3>
          <ul className="list-disc list-inside space-y-2">
            {(t("steamcat.results.bullets", { returnObjects: true }) || []).map((b, i) => (
              <li key={i} className={styles.bodyText}>{b}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default STEAMcatPage;
